import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { tokenStorage } from '@/shared/utils/tokenStorage';

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Generic API response type
export interface IApiResponse<T> {
  data: T;
  message?: string;
  status: number;
}

const API_BASE_URL = import.meta.env.VITE_BASE_URL;

const getToken = () => tokenStorage.getAccessToken();

// Function to clear all stored data when authentication fails
const clearAllStoredData = async () => {
  tokenStorage.clearAllTokens();
  // Clear ALL localStorage to ensure complete cleanup
  try {
    localStorage.clear();
  } catch {
    // If clear() fails, manually remove known items
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentprojectselected');
    localStorage.removeItem('chatbot-tooltip-shown');
    localStorage.removeItem('auth_error');
    localStorage.removeItem('theme');
  }
};

export function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 seconds timeout to prevent hanging
  });

  // Request interceptor
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = getToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(new ApiError(500, 'Request configuration error', error))
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!error.response) {
        throw new ApiError(500, 'Network error', error);
      }
      const { status, data } = error.response;

      // Check if this is a login/auth endpoint - skip token refresh for these
      const isAuthEndpoint =
        originalRequest.url?.includes('/api/ai-automation-admin/login/') ||
        originalRequest.url?.includes('/api/v1/auth/google/') ||
        originalRequest.url?.includes('/api/v1/auth/microsoft/') ||
        originalRequest.url?.includes('/api/auth/google/') ||
        originalRequest.url?.includes('/api/auth/microsoft/');

      // Handle authentication errors (but skip for auth endpoints)
      const detail = typeof data?.detail === 'string' ? data.detail : '';
      const detailLower = detail.toLowerCase();
      const isAuthError =
        status === 401 ||
        status === 403 ||
        data?.code === 'token_not_valid' ||
        detail === 'Authentication credentials were not provided.' ||
        detailLower.includes('token') ||
        detailLower.includes('expired') ||
        detailLower.includes('forbidden');

      if (isAuthError && !isAuthEndpoint) {
        clearAllStoredData();
        window.location.href = '/login';
        return Promise.reject(new ApiError(status, 'Session expired. Please login again.', data));
      }

      // Handle other errors
      const errorMessage =
        data?.error ||
        data?.message ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0
          ? data.non_field_errors[0]
          : null) ||
        data?.connection_test?.error ||
        (Array.isArray(data?.validation_warnings) ? data.validation_warnings.join(', ') : null) ||
        (Array.isArray(data?.validation_errors) ? data.validation_errors.join(', ') : null) ||
        (status === 401 ? 'Invalid credentials. Please check your email and password.' : null) ||
        (status === 400 ? 'Invalid request. Please check your input.' : null) ||
        'An unexpected error occurred';
      throw new ApiError(status, errorMessage, data);
    }
  );

  return instance;
}

// Type-safe API service
export const apiService = (api: AxiosInstance = createApiInstance()) => {
  return {
    create: async <T, R>(url: string, data?: T, headers?: Record<string, string>): Promise<R> => {
      const response = await api.post<R>(url, data, { headers });
      return response.data;
    },

    get: async <R>(url: string, params?: Record<string, unknown>): Promise<R> => {
      const response = await api.get<R>(url, { params });
      return response.data;
    },

    update: async <T, R>(
      url: string,
      id: string,
      data: T,
      headers?: Record<string, string>
    ): Promise<R> => {
      // Ensure proper URL construction: remove trailing slash from url, add id, then add trailing slash
      const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
      const updateUrl = `${cleanUrl}/${id}/`;
      const response = await api.put<R>(updateUrl, data, { headers });
      return response.data;
    },

    updateWithoutId: async <T, R>(
      url: string,
      data: T,
      headers?: Record<string, string>
    ): Promise<R> => {
      const response = await api.put<R>(url, data, { headers });
      return response.data;
    },

    delete: async <R>(
      url: string,
      id: string,
      params?: Record<string, unknown>,
      data?: Record<string, unknown>
    ): Promise<R> => {
      // Ensure proper URL construction: remove trailing slash from url, add id, then add trailing slash
      const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
      const deleteUrl = `${cleanUrl}/${id}/`;

      const config: { params?: Record<string, unknown>; data?: Record<string, unknown> } = {};
      if (params) {
        config.params = params;
      }
      if (data) {
        config.data = data;
      }

      const response = await api.delete<R>(deleteUrl, config);
      // Handle empty responses or different response formats
      return (response.data ?? {}) as R;
    },

    download: async (url: string): Promise<Blob> => {
      const response = await api.get(url, {
        responseType: 'blob',
      });
      return response.data;
    },
  };
};
